import {useCallback, useId, useState, type ReactNode, type Ref} from 'react'
import type {HoverPopoverHandlers} from '@github-ui/use-hover-popover'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IconButton, Tooltip} from '@primer/react'
import {ChevronDownIcon, PlusIcon, type Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {repoGroupCollapsedStorageKey} from '../../../utils/repo-group-expansion'
import {repoDisplayNameWithOwner} from '../../../utils/repo-group-label'
import sharedStyles from './SessionsByRepoNav.module.css'
import styles from './SessionsNavGroup.module.css'

type AnchorRegionProps = Omit<HoverPopoverHandlers<HTMLSpanElement>['anchorRegionProps'], 'onPointerEnter'> & {
  onPointerEnter: React.PointerEventHandler<HTMLSpanElement>
}

export interface SessionNavGroupProps {
  groupKey: string
  label: string
  children: ReactNode
  icon?: Icon
  variant?: 'sessions' | 'chats'
  collapsible?: boolean
  newSessionButtonVisible?: boolean
  newSessionHref?: string
  newSessionDisabled?: boolean
  onNewSessionClick?: (event: React.MouseEvent<HTMLElement>) => void
  repoNwo?: string
  /** Ref for the heading row, used to anchor an optional hovercard overlay. */
  rowRef?: Ref<HTMLDivElement>
  /** Pointer-region props spread onto the group heading. */
  anchorRegionProps?: AnchorRegionProps
  /** Focus/description props spread onto the disclosure button. */
  anchorButtonProps?: {
    onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void
    'aria-describedby'?: string
  }
  /** Rendered as a sibling of the heading row; the caller owns the overlay. */
  hovercard?: ReactNode
  /** Called before the group toggles, so a caller can dismiss its hovercard. */
  onToggle?: () => void
}

export const SessionsNavGroup = ({
  groupKey,
  label,
  children,
  icon: GroupIcon,
  variant = 'sessions',
  collapsible = true,
  newSessionButtonVisible = false,
  newSessionHref,
  newSessionDisabled,
  onNewSessionClick,
  repoNwo,
  rowRef,
  anchorRegionProps,
  anchorButtonProps,
  hovercard,
  onToggle,
}: SessionNavGroupProps) => {
  const isChatGroup = variant === 'chats'
  const persistExpansion = isFeatureEnabled('copilot_mission_control_agents_task_detail') && !isChatGroup
  const [isOpenInMemory, setIsOpenInMemory] = useState(true)
  // `groupKey` is the internal nwo, whose owner carries a tenant suffix in
  // multi-tenant environments. Key on the display nwo so it matches the plain
  // "owner/repo" the composer clears.
  const storageGroupKey = repoDisplayNameWithOwner(groupKey) ?? groupKey
  const [isCollapsedInStorage, setIsCollapsedInStorage, clearCollapsedInStorage] = useLocalStorage(
    repoGroupCollapsedStorageKey(storageGroupKey),
    false,
  )
  const isOpen = persistExpansion ? !isCollapsedInStorage : isOpenInMemory
  const setIsOpen = useCallback(
    (nextOpen: boolean) => {
      if (!persistExpansion) {
        setIsOpenInMemory(nextOpen)
      } else if (nextOpen) {
        clearCollapsedInStorage()
      } else {
        setIsCollapsedInStorage(true)
      }
    },
    [persistExpansion, clearCollapsedInStorage, setIsCollapsedInStorage],
  )
  const disclosureId = useId()
  const subNavId = useId()
  const toggleGroup = useCallback(() => {
    onToggle?.()
    setIsOpen(!isOpen)
  }, [onToggle, setIsOpen, isOpen])
  const handleNewSessionClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isChatGroup) setIsOpen(true)
      onNewSessionClick?.(event)
    },
    [isChatGroup, onNewSessionClick, setIsOpen],
  )
  const heading = (
    <span
      {...anchorRegionProps}
      className={styles.groupHeading}
      data-repo-group-heading={isChatGroup ? undefined : groupKey}
      data-session-group-heading={isChatGroup ? groupKey : undefined}
    >
      {GroupIcon && <GroupIcon aria-hidden="true" className={styles.groupIcon} data-testid="sessions-nav-group-icon" />}
      <span className={clsx(sharedStyles.truncate, styles.groupName)}>{label}</span>
    </span>
  )

  return (
    <li className={styles.group}>
      <div ref={rowRef} className={styles.row} data-testid={isChatGroup ? undefined : 'repo-row'}>
        {collapsible ? (
          <button
            id={disclosureId}
            type="button"
            aria-expanded={isOpen}
            onClick={toggleGroup}
            {...anchorButtonProps}
            className={styles.toggle}
          >
            {heading}
          </button>
        ) : (
          <span id={disclosureId} className={clsx(styles.toggle, styles.staticHeading)}>
            {heading}
          </span>
        )}
        {newSessionDisabled ? (
          <Tooltip text="Copilot cloud agent is disabled for this repository" type="description">
            <IconButton
              inactive
              aria-disabled
              icon={PlusIcon}
              aria-label={`New session in ${repoNwo} unavailable: Copilot cloud agent is disabled for this repository`}
              onClick={event => event.preventDefault()}
              variant="invisible"
              size="small"
              className={styles.newSessionButton}
              data-testid="repo-new-session-button"
            />
          </Tooltip>
        ) : (
          newSessionHref && (
            <IconButton
              as="a"
              href={newSessionHref}
              onClick={handleNewSessionClick}
              icon={PlusIcon}
              aria-label={isChatGroup ? 'New chat' : `New session in ${repoNwo}`}
              variant="invisible"
              size="small"
              className={clsx(styles.newSessionButton, newSessionButtonVisible && styles.newSessionButtonVisible)}
              data-testid={isChatGroup ? 'chat-new-button' : 'repo-new-session-button'}
            />
          )
        )}
        {collapsible && (
          <button type="button" onClick={toggleGroup} className={styles.chevronToggle} tabIndex={-1} aria-hidden="true">
            <ChevronDownIcon className={clsx(styles.chevron, !isOpen && styles.chevronCollapsed)} />
          </button>
        )}
      </div>
      {hovercard}
      {(collapsible || children != null) && (
        <ul
          id={subNavId}
          aria-labelledby={disclosureId}
          data-component="NavList.SubNav"
          hidden={collapsible && !isOpen}
        >
          {children}
        </ul>
      )}
    </li>
  )
}
